/**
 * Facility API Service
 */

import apiClient from './apiClient';
import {
  FacilityDTO,
  CreateFacilityRequest,
  PageResponse,
} from '../types/facility.types';

const BASE_PATH = '/v1/facilities';

class FacilityService {
  async createFacility(request: CreateFacilityRequest): Promise<FacilityDTO> {
    const response = await apiClient.post<FacilityDTO>(BASE_PATH, request);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
    return response.data;
  }

  async getFacilityById(id: string): Promise<FacilityDTO> {
    const response = await apiClient.get<FacilityDTO>(`${BASE_PATH}/${id}`, {
      headers: { 'X-Tenant-Id': id }
    });
    return response.data;
  }

  async getFacilityByCode(code: string): Promise<FacilityDTO> {
    const response = await apiClient.get<FacilityDTO>(`${BASE_PATH}/code/${code}`);
    return response.data;
  }

  async getAllFacilities(page: number = 0, size: number = 20): Promise<PageResponse<FacilityDTO>> {
    const response = await apiClient.get<PageResponse<FacilityDTO>>(BASE_PATH, {
      params: { page, size, sort: 'createdAt' },
    });
    return response.data;
  }

  async getFacilitiesByClient(clientId: string): Promise<FacilityDTO[]> {
    const response = await apiClient.get<FacilityDTO[]>(`${BASE_PATH}/client/${clientId}`);
    return response.data;
  }

  async getFacilitiesByUser(userId: string): Promise<FacilityDTO[]> {
    const response = await apiClient.get<FacilityDTO[]>(`${BASE_PATH}/users/${userId}`);
    return response.data;
  }

  async getActiveFacilities(): Promise<FacilityDTO[]> {
    const response = await apiClient.get<FacilityDTO[]>(`${BASE_PATH}/active-facilities`);
    return response.data;
  }

  async updateFacility(id: string, data: Partial<FacilityDTO>): Promise<FacilityDTO> {
    const response = await apiClient.put<FacilityDTO>(`${BASE_PATH}/${id}`, data, {
      headers: { 'X-Tenant-Id': id }
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
    return response.data;
  }

  async uploadFacilityLogo(id: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE_PATH}/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Tenant-Id': id
      },
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
    return response.data;
  }

  async setFacilityAsPrimary(id: string, isPrimary: boolean = true): Promise<any> {
    const response = await apiClient.patch(`${BASE_PATH}/${id}/primary`, null, {
      params: { isPrimary },
      headers: { 'X-Tenant-Id': id }
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
    return response.data;
  }

  /**
   * Returns the URL for a facility's logo endpoint.
   * The browser will send the auth token via the apiClient interceptors,
   * but since we need the URL as an <img src>, we use a Blob URL approach
   * in the consuming component instead.
   */
  async getFacilityLogoBlob(id: string): Promise<string | null> {
    try {
      const response = await apiClient.get(`${BASE_PATH}/${id}/logo`, {
        responseType: 'blob',
        headers: {
          'X-Tenant-Id': id
        }
      });
      const blob = response.data as Blob;
      if (!blob || blob.size === 0) return null;
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  async deleteFacilityLogo(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}/logo`, {
      headers: { 'X-Tenant-Id': id }
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("facilitiesUpdated"));
    }
  }

  async deleteFacility(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`, {
      headers: { 'X-Tenant-Id': id }
    });
  }
}

export default new FacilityService();